import { Reveal, Label } from "../components/site/Reveal";
import { SITE } from "../config/site";

const sections = [
  {
    title: "Agreement and eligibility",
    body: [
      `These Terms of Use ("Terms") govern your use of the ${SITE.name} mobile app and website. By using ${SITE.name}, you agree to these Terms. You must be at least 13 years old to use ${SITE.name}; if you are under 18 (or the age of majority where you live), you may use the app only with the involvement and consent of a parent or legal guardian.`,
    ],
  },
  {
    title: "User accounts",
    body: [
      "Some features require an account. You agree to provide accurate information when registering, to keep your sign-in credentials confidential, and to notify us if you suspect unauthorized access. You are responsible for activity that occurs under your account.",
    ],
  },
  {
    title: "Acceptable use",
    body: [
      "You agree to use NXTPlate only for its intended personal wellness purpose and in compliance with applicable law. You will not misuse the app, attempt to access systems or data that are not yours, reverse engineer the service except where permitted by law, upload unlawful or harmful content, or use the app in a way that interferes with other users or our infrastructure.",
    ],
  },
  {
    title: "Subscriptions and billing",
    body: [
      "NXTPlate offers free access alongside paid plans, including NXTPlate Pro and NXTPlate AI Coach. Paid features, plan details, and pricing are presented in the app before you subscribe. By starting a paid subscription, you authorize the applicable charges through your app store account.",
    ],
  },
  {
    title: "Apple in-app purchases",
    body: [
      "Subscriptions purchased on iOS are billed by Apple as in-app purchases through your Apple ID. Your purchase is also subject to Apple's terms and policies. Payment card details are handled by Apple; NXTPlate does not receive or store them. Refund requests for Apple in-app purchases are handled by Apple under its own policies.",
    ],
  },
  {
    title: "Automatic renewal",
    body: [
      "Paid subscriptions renew automatically at the end of each billing period unless you cancel at least 24 hours before the renewal date. Your Apple ID account will be charged for renewal within 24 hours prior to the end of the current period, at the then-current price for your plan.",
    ],
  },
  {
    title: "Cancellation",
    body: [
      "You can manage or cancel your subscription at any time through your Apple ID settings: open the Settings app on your iPhone, tap your name, tap Subscriptions, and select NXTPlate. Cancellation takes effect at the end of the current billing period — you keep access to paid features until then. Deleting the app or your account does not cancel an active subscription.",
    ],
  },
  {
    title: "AI-generated content disclaimer",
    body: [
      "NXTPlate generates recommendations — including next-meal guidance and AI Coach responses — using automated systems based on the information you provide. AI-generated content may be incomplete, inaccurate, or unsuitable for your circumstances. It is provided for general informational and wellness purposes only, and you use it at your own discretion.",
    ],
  },
  {
    title: "Nutrition and wellness disclaimer — no medical advice",
    body: [
      "NXTPlate is a wellness and nutrition app. It does not provide medical advice, medical diagnosis, or treatment, and it is not a substitute for professional care. Nutritional estimates — including those generated from photos — are approximations. Always consult a qualified healthcare professional before making significant changes to your diet, especially if you have a medical condition, are pregnant, or have a history of disordered eating. Never disregard professional medical advice because of something you saw in the app.",
    ],
  },
  {
    title: "Limitation of liability",
    body: [
      "To the maximum extent permitted by law, NXTPlate and its operators will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of data, profits, or goodwill, arising from your use of the app. Our total liability for any claim relating to the service is limited to the amount you paid us for the service in the twelve months before the claim, or, if you paid nothing, the minimum amount permitted by applicable law. Some jurisdictions do not allow certain limitations, so parts of this section may not apply to you.",
    ],
  },
  {
    title: "Intellectual property",
    body: [
      "The NXTPlate name, logo, app, website, and their content and design are owned by us and protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable license to use the app for personal, non-commercial purposes. You retain ownership of the content you add, and grant us the limited rights needed to operate the service for you.",
    ],
  },
  {
    title: "Termination",
    body: [
      "You may stop using NXTPlate and delete your account at any time. We may suspend or terminate access if you breach these Terms or use the service in a way that could harm other users or us. Where reasonable, we will give notice before termination. Provisions that by their nature should survive termination — including disclaimers, limitation of liability, and intellectual property — will survive.",
    ],
  },
  {
    title: "Changes to these Terms",
    body: [
      "We may update these Terms as the service evolves. If changes are material, we will notify you in the app or by email before they take effect. Continuing to use NXTPlate after updated Terms take effect means you accept them.",
    ],
  },
];

export default function Terms() {
  return (
    <main className="bg-cream">
      <section className="noise relative overflow-hidden pt-32 md:pt-44 pb-12 md:pb-16">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Reveal><Label>Legal</Label></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl font-medium tracking-tight text-neutral-900 md:text-6xl">
              Terms of <span className="italic text-coral">Use</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-4 text-sm font-medium uppercase tracking-[0.18em] text-neutral-400">
              Last updated: {SITE.legalLastUpdated}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="terms-content" className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          {sections.map((s, i) => (
            <Reveal key={s.title} className="border-t border-neutral-200 py-10 first:border-t-0 first:pt-0">
              <div data-testid={`terms-section-${i + 1}`}>
                <h2 className="font-display text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
                  <span className="mr-3 font-display text-base italic text-coral">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </h2>
                {s.body.map((p, j) => (
                  <p key={j} className="mt-4 max-w-3xl text-base leading-relaxed text-neutral-600">{p}</p>
                ))}
              </div>
            </Reveal>
          ))}

          <Reveal className="border-t border-neutral-200 py-10">
            <div data-testid="terms-contact">
              <h2 className="font-display text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
                <span className="mr-3 font-display text-base italic text-coral">{String(sections.length + 1).padStart(2, "0")}</span>
                Contact us
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-neutral-600">
                Questions about these Terms? Email us at{" "}
                <a
                  href={`mailto:${SITE.supportEmail}`}
                  data-testid="terms-email-link"
                  className="font-semibold text-neutral-900 underline decoration-coral decoration-2 underline-offset-4 transition-colors duration-200 hover:text-coral"
                >
                  {SITE.supportEmail}
                </a>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
